import React, { ReactNode, useState } from 'react'
import { IoIosCheckmarkCircleOutline } from '@react-icons/all-files/io/IoIosCheckmarkCircleOutline'
import { IoIosCloseCircleOutline } from '@react-icons/all-files/io/IoIosCloseCircleOutline'
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline'
import { RxMagicWand } from '@react-icons/all-files/rx/RxMagicWand'

import {
  AccessibleIcon,
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  IconButton,
  Link,
  Separator,
  Spinner,
  Text,
  TextArea,
  Tooltip as RadixTooltip,
} from 'components/ds'
import { getTextAreaProps } from 'components/ds/AiSuggestTextArea/utils'
import { extractMarginProps } from 'components/ds/helpers'

type Props = React.ComponentProps<typeof TextArea> & {
  width?: React.ComponentProps<typeof Flex>['width']
  processing?: boolean
  processingText?: ReactNode
  emptyText?: ReactNode
  suggestionText?: ReactNode
  noSuggestionText?: ReactNode
  editWarningText?: ReactNode
  lowConfidenceText?: ReactNode
  confidence?: number
  onSuggest: () => void
  onAccept: () => void
  onReject: () => void
  onDismiss: () => void
  lowestConfidence?: number
  aiDisabled?: boolean
  quotaAvailable?: number | null
  quotaText?: React.ReactNode
  generationDisabledTooltipText?: React.ReactNode
  generateButtonLabel?: string
}

const ConfidenceBadge = ({ confidence }: { confidence: number }) => {
  let text = 'LOW CONFIDENCE'
  let color: React.ComponentProps<typeof Badge>['color'] = 'mint'
  let variant: React.ComponentProps<typeof Badge>['variant'] = 'soft'
  let highContrast: React.ComponentProps<typeof Badge>['highContrast'] = false

  if (confidence >= 0.9) {
    text = 'MEDIUM CONFIDENCE'
    variant = 'solid'
  }

  if (confidence >= 0.95) {
    text = 'HIGH CONFIDENCE'
    color = 'green'
    variant = 'solid'
  }

  return (
    <Badge color={color} variant={variant} highContrast={highContrast} size="2">
      <RxMagicWand /> {text}
    </Badge>
  )
}

const Tooltip = props => {
  if (!props.content) return props.children
  return <RadixTooltip {...props} />
}

/**
 * AI-powered text area component with AI suggestions, confidence indicators, and user approval/rejection controls.
 *
 * Provides features for AI-assisted text generation with quota management and confidence visualization.
 * Includes processing states, confidence badges, and interactive controls for accepting or rejecting suggestions.
 *
 * @param width - Width of the component container
 * @param processing - Whether AI is currently processing a request
 * @param processingText - Text to display during processing
 * @param emptyText - Text to display when no content is present
 * @param suggestionText - Label for AI suggestions
 * @param noSuggestionText - Text when no suggestion is available
 * @param editWarningText - Warning text about editing restrictions
 * @param lowConfidenceText - Text for low confidence suggestions
 * @param onSuggest - Callback when user requests AI suggestion
 * @param onAccept - Callback when user accepts AI suggestion
 * @param onReject - Callback when user rejects AI suggestion
 * @param onDismiss - Callback when user dismisses low confidence warning
 * @param confidence - AI confidence score (0-1)
 * @param lowestConfidence - Minimum confidence threshold
 * @param aiDisabled - Whether AI features are disabled
 * @param quotaAvailable - Available AI quota count
 * @param quotaText - Text describing quota usage
 * @param generationDisabledTooltipText - Tooltip when generation is disabled
 * @param generateButtonLabel - Label for the generate button
 */
const AiSuggestTextArea = ({
  width,
  style,
  className,
  processing = false,
  processingText = 'Processing...',
  emptyText = 'Generate?',
  suggestionText = 'Suggestion',
  noSuggestionText = 'No suggestion.',
  editWarningText = 'You can edit after accepting or rejecting.',
  lowConfidenceText = 'Generated, but did not meet our quality standards.',
  onSuggest,
  onAccept,
  onReject,
  onDismiss,
  confidence,
  lowestConfidence = 0,
  aiDisabled = false,
  quotaAvailable = null,
  quotaText,
  generationDisabledTooltipText,
  generateButtonLabel = 'Generate',
  ...rest
}: Props): React.ReactNode => {
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const disabled = processing || confidence
  const confidenceProvided = confidence !== undefined
  const isLowConfidence = confidenceProvided && confidence < lowestConfidence
  const isNoResult = confidence === 1 && rest.value === ''
  const showBar =
    !aiDisabled &&
    (!rest.value || processing || confidenceProvided) &&
    !isLowConfidence
  const canGenerate = quotaAvailable === null || quotaAvailable > 0
  // Split the rest into margin and other props
  const { rest: restWithoutMarginProps, ...marginProps } =
    extractMarginProps(rest)

  const textAreaProps = getTextAreaProps(restWithoutMarginProps)

  return (
    <Flex
      direction="column"
      gap="3"
      width={width}
      style={{ ...style, position: 'relative', height: '100%' }}
      className={className}
      {...marginProps}
    >
      <Tooltip
        open={tooltipOpen}
        onOpenChange={setTooltipOpen}
        side="top"
        avoidCollisions={true}
        onPointerDownOutside={e => e.preventDefault()}
        content={confidence && showBar ? editWarningText : undefined}
      >
        <Box
          position="relative"
          onClick={e => {
            e.preventDefault()
            setTooltipOpen(true)
          }}
        >
          <TextArea
            {...textAreaProps}
            readOnly={Boolean(disabled) || rest.disabled}
          />
        </Box>
      </Tooltip>
      {isLowConfidence && (
        <Callout.Root>
          <Callout.Icon>
            <MdInfoOutline />
          </Callout.Icon>
          <Callout.Text>
            {lowConfidenceText}{' '}
            {!processing && (
              <Link
                href="#"
                onClick={e => {
                  e.preventDefault()
                  onDismiss()
                }}
              >
                Dismiss
              </Link>
            )}
          </Callout.Text>
        </Callout.Root>
      )}
      {showBar && (
        <Flex minHeight="50px" direction="column" width="100%">
          <Card size="2">
            <Flex gap="3" align="center">
              {confidence === undefined && (
                <AccessibleIcon label={generateButtonLabel}>
                  <RxMagicWand color="var(--accent-9)" />
                </AccessibleIcon>
              )}
              <Flex flexGrow="1" align="center" gap="2">
                <Text size="2" color="gray">
                  {confidence
                    ? isNoResult
                      ? noSuggestionText
                      : suggestionText
                    : processing
                    ? processingText
                    : emptyText}
                </Text>
                {confidence !== undefined && confidence > 0 && !isNoResult && (
                  <ConfidenceBadge confidence={confidence} />
                )}
              </Flex>
              <Flex align="center" justify="center" gap="1">
                {processing ? (
                  <Spinner />
                ) : confidence ? (
                  <>
                    <IconButton type="button" size="1" onClick={onAccept}>
                      <AccessibleIcon label="Accept">
                        <IoIosCheckmarkCircleOutline />
                      </AccessibleIcon>
                    </IconButton>
                    <IconButton
                      color="red"
                      type="button"
                      size="1"
                      onClick={onReject}
                    >
                      <AccessibleIcon label="Reject">
                        <IoIosCloseCircleOutline />
                      </AccessibleIcon>
                    </IconButton>
                  </>
                ) : (
                  <Tooltip
                    content={canGenerate ? null : generationDisabledTooltipText}
                  >
                    <Button
                      size="1"
                      onClick={onSuggest}
                      disabled={!canGenerate}
                    >
                      {generateButtonLabel}
                    </Button>
                  </Tooltip>
                )}
              </Flex>
            </Flex>
            {quotaAvailable !== null && (
              <>
                <Separator size="4" my="3" />
                <Flex gap="3" align="center" justify="between">
                  <Text size="1" color="gray">
                    {quotaText}
                  </Text>
                  <Text size="1" color={quotaAvailable > 0 ? 'indigo' : 'red'}>
                    <Text weight="bold">{Math.max(quotaAvailable, 0)}</Text>{' '}
                    Available
                  </Text>
                </Flex>
              </>
            )}
          </Card>
        </Flex>
      )}
    </Flex>
  )
}

export default AiSuggestTextArea
